import { createContext, useContext, useLayoutEffect } from 'react';

/**
 * A host that stacks several inline surfaces (the preferences page) collects
 * each one's activation wait into one spinner. Absent everywhere else, so a
 * surface keeps its own indicator.
 *
 * Reported from `useLayoutEffect`: the per-section spinner is committed, then
 * replaced, before the browser paints. No timeout — a view that never reports
 * does not hold the page, and a reported wait ends only when that view leaves
 * the activating state (mounted, or failed).
 */
export const PageViewLoadingContext = createContext<
  ((viewId: string, loading: boolean) => void) | null
>(null);

/** @returns whether a page host is collecting this surface's activation wait. */
export function usePageViewLoading(viewId: string, loading: boolean): boolean {
  const report = useContext(PageViewLoadingContext);
  useLayoutEffect(() => {
    if (!report) {
      return;
    }
    report(viewId, loading);
    return () => {
      report(viewId, false);
    };
  }, [loading, report, viewId]);
  return report !== null;
}
